import { useCart } from '@shared/cart-react';
import {
  Button,
  cn,
  Dots,
  Icon,
  Input,
  Modal,
  SmoothMount
} from '@shared/cove-ui';
import { useProducts } from '@shared/react-product';
import { cx } from 'class-variance-authority';
import { add, format, set } from 'date-fns';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { createCommaList, roundTo } from '../../../helpers/tools';
import PrettyPrice from '../PrettyPrice';
import Price from '../Price';
import ProductLightbox from '../ProductLightbox';
import CartBreakdown from './CartBreakdown';
import CartTotalsCollapsible from './CartTotalsCollapsible';
import Test108 from './variants/test-108';

export const getNumberWithOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];

  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getFullDate = (date: Date, freeMonths: number) => {
  const day = date.getDate();
  let month = date.getMonth();
  month += freeMonths;
  const finalDate = set(date, { month, date: day });
  return format(finalDate, 'MM.dd.yy');
};

/**
 * Prefer passing `isPaymentPage` / `isCustomerInfoPage` props instead.
 * Reading `window` during render breaks SSR hydration: server always gets
 * false, client gets the real path, so payment/customer-info trees diverge.
 */
export const isCurrentPage = (path: string, pathname?: string) => {
  const currentPath =
    pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined);

  if (!currentPath) {
    return false;
  }

  return currentPath === path || currentPath.startsWith(path);
};

type CartTotalsPageFlags = {
  isPaymentPage: boolean;
  isCustomerInfoPage: boolean;
};

const CartTotalsPageContext = createContext<CartTotalsPageFlags>({
  isPaymentPage: false,
  isCustomerInfoPage: false
});

const useCartTotalsPageFlags = () => useContext(CartTotalsPageContext);

const PRODUCT_DISPLAY_NAMES = {
  'CP-PANEL-345-1': 'Cove Touch Panel',
  'CP-KEY-345-1': 'Key Remote',
  '2G-CO-345-1': 'CO Detector',
  '2G-SHF-345-1': 'Smoke Detector'
};

type Cart = ReturnType<typeof useCart>;

export type CartTotalsWidgetProps = {
  maximize?: boolean;
  showSavings?: boolean;
  productPackage?: { name: string; products: [] };
  cart: Cart;
  variant?: 'normal' | 'breakdown' | 'collapsible' | '97-a' | 'control';
  tests?: Record<string, string>;
  showPromoInput?: boolean;
  showEquipmentBreakdown?: boolean;
  showDiscountBreakdown?: boolean;
  autoAppliedDiscountTitle?: string;
  useOrder?: boolean;
  hideAvailableCoupons?: boolean;
  /** Pass from the page (SSR-safe). Do not derive from `window` during render. */
  isPaymentPage?: boolean;
  /** Pass from the page (SSR-safe). Do not derive from `window` during render. */
  isCustomerInfoPage?: boolean;
};

const CartTotalsWidget: React.FC<CartTotalsWidgetProps> = ({
  maximize = false,
  productPackage,
  showPromoInput = false,
  cart: incomingCart,
  variant = 'normal',
  tests = {},
  useOrder = false,
  hideAvailableCoupons = false,
  isPaymentPage = false,
  isCustomerInfoPage = false
}) => {
  const isNormal = variant === 'normal';
  const isBreakdown = variant === 'breakdown';
  const isCollapsible = variant === 'collapsible';
  const [expanded, setExpanded] = useState(maximize);
  const [cartTotalsExpanded, setCartTotalsExpanded] = useState(
    isPaymentPage ? false : true
  );

  const pageFlags = useMemo(
    () => ({ isPaymentPage, isCustomerInfoPage }),
    [isPaymentPage, isCustomerInfoPage]
  );

  const orderOrCart = useOrderOrCart();

  const cart = useOrder ? orderOrCart : incomingCart;

  useEffect(() => {
    setExpanded(maximize);
  }, [maximize]);

  // Post-checkout (`useOrder`) must not mutate the live cart — it may already
  // have been cleared after payment, and writing a default plan here is waste.
  useEffect(() => {
    if (useOrder) return;
    if (
      isEmpty(incomingCart) &&
      (isEmpty(incomingCart?.monitoringPlan) ||
        isUndefined(incomingCart?.monitoringPlan))
    ) {
      incomingCart?.updateMonitoringPlan?.('vm-pn');
    }
  }, [useOrder, incomingCart, incomingCart?.monitoringPlan?.sku]);

  const totals = cart?.totals?.totals || {
    orderGrandTotal: 0,
    orderRetailTotal: 0,
    orderTotalWithTax: 0,
    equipmentTotalTax: 0,
    equipmentBaseTotal: 0,
    allEquipmentGrandTotal: 0,
    equipmentGrandTotal: 0,
    equipmentRetailTotal: 0,
    equipmentTotalWithTax: 0,
    monitoringTotalWithTax: 0,
    monitoringRetailTotalTax: 0,
    payLaterBalanceWithTax: 0
  };
  const isTaxCalculated = cart?.taxCalculated || false;
  const totalDiscount = totals?.orderRetailTotal - totals?.orderTotalWithTax;

  const hasAffirmTransaction =
    typeof cart?.affirmAmount === 'number' && cart?.affirmAmount > 0;
  const hasPayPalTransaction =
    typeof cart?.paypalAmount === 'number' && cart?.paypalAmount > 0;

  // Giveaway UI is currently disabled (component returns null). Skip mounting
  // it so we don't pay for an extra useOrderOrCart localStorage parse.
  const showGiveawayEntries = false;

  const equipmentDetailsProducts =
    tests['108'] === 'a'
      ? Object.values(cart?.products ?? {})
          .filter(
            (product) =>
              !exclude.includes(product.sku) && !!cart.cartProducts[product.sku]
          )
          .sort((a, b) => a.position - b.position)
      : [];

  const freeMonths = cart?.freeMonths ?? 0;

  if (tests['108'] === 'a') {
    return (
      <CartTotalsPageContext.Provider value={pageFlags}>
        <Test108
          totals={totals}
          isTaxCalculated={isTaxCalculated}
          totalDiscount={totalDiscount}
          equipmentDetailsProducts={equipmentDetailsProducts}
          freeMonths={freeMonths}
          isCollapsible={isCollapsible}
          isBreakdown={isBreakdown}
          useOrder={useOrder}
        />
      </CartTotalsPageContext.Provider>
    );
  }

  return (
    <CartTotalsPageContext.Provider value={pageFlags}>
      <div className="mx-auto mt-0 w-full max-w-[580px]">
        <div
          className={cn(
            'border-cove-navy-blue overflow-hidden rounded border bg-white shadow'
          )}
        >
          <div className="w-full">
            <div
              className={cn(
                'xs:gap-cove-5 flex w-full flex-col items-center justify-center',

                isPaymentPage
                  ? 'p-cove-15'
                  : 'pt-cove-10 xs:pt-cove-25 px-cove-15 xs:px-cove-25'
              )}
            >
              <div className="flex w-full items-center justify-between">
                {isPaymentPage ? (
                  <Button
                    onClick={() => setCartTotalsExpanded(!cartTotalsExpanded)}
                    variant="unstyled"
                    className="w-full"
                  >
                    <div className="gap-cove-10 justify-left flex items-center">
                      <h5
                        className={cn(
                          'text-h5 mb-0',

                          isPaymentPage &&
                            'text-left text-base font-semibold md:text-xl'
                        )}
                      >
                        Order Summary
                      </h5>

                      <Icon
                        className={cn(
                          'text-cove-dark-grey transition-all duration-500 ease-in-out',

                          cartTotalsExpanded && 'text-cove-blue rotate-180'
                        )}
                        icon="ChevronDown"
                      />
                    </div>
                  </Button>
                ) : (
                  <h5 className="text-h5 mb-0">One Time Purchase</h5>
                )}

                <div
                  aria-hidden="true"
                  className={cn(
                    'relative flex items-start',

                    isPaymentPage && 'flex-col items-end justify-center'
                  )}
                >
                  <Price
                    price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
                    salePrice={
                      cart?.getTodaysGrandTotal?.() ||
                      totals.orderTotalWithTax ||
                      0
                    }
                    className="first:[&_span]:first-of-type:text-cove-dark-grey text-cove-green m-0 text-[16px] font-semibold [&_span]:sm:text-[20px] first:[&_span]:first-of-type:sm:text-base"
                  />

                  {!isPaymentPage && (
                    <SmoothMount
                      height={false}
                      fade
                      show={isTaxCalculated}
                      className="pb-cove-5 absolute -bottom-[30px] right-0 h-fit w-20 shrink-0 text-right"
                    >
                      <div className="flex flex-col items-end justify-center">
                        <span className="text-cove-navy-blue mt-cove-2 m-0 block text-right text-xs leading-3">
                          Including tax:
                        </span>

                        <span className="text-cove-navy-blue mt-cove-2 m-0 block text-right text-xs leading-3">
                          ${roundTo(totals.equipmentTotalTax)}
                        </span>
                      </div>
                    </SmoothMount>
                  )}
                </div>
              </div>
              <p className="sr-only">{`$${roundTo(
                totals.equipmentGrandTotal
              )}, regularly $${roundTo(totals.equipmentBaseTotal)}`}</p>
            </div>

            {/*
            Skip enter animation when this section starts open (order-summary /
            non-payment). Framer Motion's height:0 initial vs animate height:auto
            is a common SSR/client hydration mismatch (#418/#423).
          */}
            <SmoothMount show={cartTotalsExpanded} initialAnimation={false}>
              {isPaymentPage ? (
                <div className="py-cove-10 px-cove-15 xs:px-cove-25 xs:pb-cove-10 pb-cove-10 flex w-full items-start justify-between ">
                  <EquipmentDetails variant={variant} useOrder={useOrder} />
                </div>
              ) : (
                <>
                  <div className="px-cove-15 xs:px-cove-25 flex w-full items-start justify-between">
                    <EquipmentDetails variant={variant} useOrder={useOrder} />
                  </div>

                  <span className="bg-cove-dove block h-[1px] w-full" />
                </>
              )}

              <Monitoring useOrder={useOrder} />

              {!isPaymentPage && (
                <span className="bg-cove-dove block h-[1px] w-full" />
              )}

              <div className="py-cove-10 xs:py-cove-25 px-cove-15 xs:px-cove-25 flex items-center justify-between">
                <div className="gap-cove-5 flex items-center">
                  <h5 className="text-h5 m-0">Shipping</h5>

                  <span className="text-cove-navy-blue m-0 text-[12px] font-medium">
                    2 - 7 business days
                  </span>
                </div>

                <p className="text-cove-green m-0 text-right font-semibold">
                  Free
                </p>
              </div>

              {!isPaymentPage && (
                <span className="bg-cove-dove block h-[1px] w-full" />
              )}

              <PromoCodeBreakdown />

              <div className="py-cove-10 xs:py-cove-25 px-cove-15 xs:px-cove-25 flex items-center justify-between">
                <h5 className="text-h5 mb-0">
                  {isPaymentPage ? "Today's Savings:" : 'Discount'}
                </h5>

                <span className="text-cove-red">
                  -
                  <PrettyPrice
                    price={totalDiscount || 0}
                    className="text-cove-red text-base font-semibold"
                  />
                </span>
              </div>

              {isPaymentPage ? (
                <span className="bg-cove-dove mx-auto block h-[1px] w-[calc(100%-50px)]" />
              ) : (
                <span className="bg-cove-dove block h-[1px] w-full" />
              )}

              <div className="px-cove-15 xs:px-cove-25">
                {showGiveawayEntries && <GiveawayEntries />}
              </div>

              <div className="px-cove-15 xs:px-cove-25 py-cove-10 xs:py-cove-25">
                <div
                  className={cx(
                    'flex items-center justify-between',

                    maximize ? 'mb-cove-0' : 'mb-cove-15'
                  )}
                >
                  <h2 className="xs:text-xl m-0 text-[16px] font-semibold">
                    {isPaymentPage ? 'Order Total' : 'Pay Today'}
                  </h2>

                  <Price
                    price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
                    salePrice={
                      cart?.getTodaysGrandTotal?.() ||
                      totals.orderTotalWithTax ||
                      0
                    }
                    className="first:[&_span]:first-of-type:text-cove-dark-grey text-cove-green m-0 text-[16px] font-semibold [&_span]:sm:text-[20px] first:[&_span]:first-of-type:sm:text-[18px]"
                  />
                </div>

                {hasAffirmTransaction && (
                  <div className="mb-cove-15 flex items-center justify-between">
                    <h5 className="text-h5">Affirm</h5>

                    <div className="flex items-center">
                      -<PrettyPrice price={cart?.affirmAmount || 0} />
                    </div>
                  </div>
                )}

                {hasPayPalTransaction && (
                  <div className="mb-cove-15 flex items-center justify-between">
                    <h5 className="text-h5">PayPal</h5>
                    <div className="flex items-center">
                      -<PrettyPrice price={cart?.paypalAmount || 0} />
                    </div>
                  </div>
                )}

                {!maximize && (
                  <>
                    <SmoothMount
                      fade
                      showOverflow
                      show={!expanded}
                      className="flex w-full flex-col justify-center"
                    >
                      <Button
                        id="review-order-widget"
                        variant="text"
                        color="blue"
                        className="mx-auto text-center underline"
                        onClick={() => {
                          setExpanded(!expanded);
                        }}
                      >
                        {expanded ? 'Hide' : 'Review'} order
                      </Button>
                    </SmoothMount>

                    <SmoothMount fade show={expanded}>
                      <div
                        className={cn(
                          'border-cove-dark-grey px-cove-10 py-cove-15 relative border',

                          hasAffirmTransaction &&
                            'mb-cove-15 pb-cove-15 border-b-cove-dove border-b'
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setExpanded(false)}
                          aria-label="Minimize order"
                          className="text-cove-blue absolute right-0 top-0 m-0 cursor-pointer border-none bg-transparent p-0"
                        >
                          <Icon icon="Cancel" width={32} height={32} />
                        </button>

                        <EquipmentBreakdown
                          useOrder={useOrder}
                          productPackage={productPackage}
                        />
                      </div>
                    </SmoothMount>
                  </>
                )}

                {showPromoInput && <PromoCodeInput variant="base" />}

                {!hideAvailableCoupons && <AvailablePromotions />}
              </div>
            </SmoothMount>
          </div>
          {isBreakdown && <CartBreakdown />}
          {isCollapsible && <CartTotalsCollapsible useOrder={useOrder} />}
        </div>
      </div>
    </CartTotalsPageContext.Provider>
  );
};

const useOrderOrCart = () => {
  const currentCart = useCart();
  const [order, setOrder] = useState<null | Cart>(null);
  const cart = { ...currentCart, ...order };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const parsed = JSON.parse(localStorage.getItem('ORDER') || '{}');
      if (parsed?.cart) {
        setOrder(parsed.cart as Cart);
      }
    } catch {
      // Corrupt ORDER in localStorage — keep live cart.
    }
  }, [currentCart]);

  return cart;
};

type AvailableCouponsBreakdown = Array<{
  couponCode: string;
  disclaimer?: string;
  totalSavings: number;
  description: string;
}>;

const exclude = [
  'window-sticker-sm',
  'vm-pn',
  'CP-YS-23i-1',
  'bm-pn',
  'bm-pl',
  'vm-pl',
  'vm-gp'
];

const EquipmentDetails = ({
  useOrder = false
}: {
  variant?: string;
  useOrder?: boolean;
}) => {
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const products = useProducts();

  const { isPaymentPage } = useCartTotalsPageFlags();

  const [expanded, setExpanded] = useState(isPaymentPage ? false : true);

  const equipment = useMemo(() => {
    return Object.values(cart?.cartProducts ?? {}).filter(
      (product) => !exclude.includes(product.sku)
    );
  }, [cart?.cartProducts]);

  const panel = equipment?.find((product) => product.sku === 'CPA-PANEL-345-1');
  const hub = equipment?.find((product) => product.sku === 'CPA-HUB-345-1');

  const orderedEquipment = [...equipment]?.filter(
    (product) =>
      product.sku !== 'CPA-PANEL-345-1' && product.sku !== 'CPA-HUB-345-1'
  );

  if (panel && hub) {
    orderedEquipment.unshift(hub, panel);
  }

  return equipment.length > 0 ? (
    <div className={cn('mb-cove-15 flex w-full flex-col')}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'text-cove-blue gap-cove-10 mb-0 flex items-center bg-transparent font-medium'
        )}
      >
        <span
          className={cn(
            'text-cove-dark-grey text-[14px] transition-all duration-300 ease-in-out',

            expanded && 'text-cove-blue',

            isPaymentPage && 'text-cove-navy-blue text-base font-bold'
          )}
        >
          {expanded ? 'Hide' : 'View'} equipment details{' '}
        </span>
        <Icon
          className={cn(
            'text-cove-dark-grey transition-all duration-500 ease-in-out',

            expanded && 'text-cove-blue rotate-180'
          )}
          icon="ChevronDown"
        />
      </button>
      {cart?.cartProducts && (
        <SmoothMount
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          show={expanded}
          // Start expanded on non-payment pages — skip height:0 enter flash.
          initialAnimation={false}
        >
          {orderedEquipment?.map((product, i) => {
            const { sku, qty, name } = product;
            return (
              <div
                key={product.sku}
                className={cx(
                  'border-cove-dove py-cove-15 px-cove-2 flex w-full justify-between',
                  i !== 0 && 'border-t'
                )}
              >
                <div className="flex items-center">
                  <div className="border-cove-dove p-cove-2 mr-cove-15 rounded-[5px] border">
                    <span className="flex h-[45px] w-[45px] items-center justify-center">
                      <img
                        className="max-w-[45px]"
                        src={products[sku]?.primaryImage?.url}
                        alt={name}
                      />
                    </span>
                  </div>
                  <p className="text-cove-navy-blue m-0 text-[12px] font-medium sm:text-base sm:font-medium">
                    {name}
                  </p>
                  <ProductLightbox
                    sku={
                      product.sku as ComponentProps<
                        typeof ProductLightbox
                      >['sku']
                    }
                  >
                    <ProductLightbox.Trigger className="ml-cove-5 bg-transparent">
                      <Icon icon="Info" className="mt-1.5" />
                      <span className="sr-only">
                        View {product.name} product details
                      </span>
                    </ProductLightbox.Trigger>
                  </ProductLightbox>
                </div>
                <p className="text-cove-navy-blue m-0 self-center text-[12px] font-medium sm:text-base sm:font-medium">
                  {qty}
                </p>
              </div>
            );
          })}
        </SmoothMount>
      )}
    </div>
  ) : null;
};

const Monitoring = ({ useOrder = false }: { useOrder?: boolean }) => {
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const [expanded, setExpanded] = useState(true);
  const isTaxCalculated = cart?.taxCalculated || false;
  const freeMonths = cart?.freeMonths ?? 0;
  const totals = cart?.totals?.totals || {
    monitoringTotalWithTax: 0,
    monitoringRetailTotalTax: 0,
    monitoringRetailTotalWithTax: 0,
    monitoringTotalTax: 0
  };

  const monitoringBasePrice = totals.monitoringRetailTotalWithTax;
  const monitoringWithDiscountPrice = totals.monitoringTotalWithTax;

  return (
    <div className="py-cove-10 xs:py-cove-25 px-cove-15 xs:px-cove-25">
      <div className={cn('flex w-full flex-col items-start justify-start')}>
        <div className="gap-cove-15 flex w-full items-center justify-between">
          <Button
            variant="unstyled"
            className="gap-cove-10 flex w-auto items-center justify-start"
            onClick={() => setExpanded(!expanded)}
          >
            <h5 className="text-h5 gap-cove-10 m-0 flex w-min text-left sm:w-auto">
              Monthly Monitoring{' '}
              <Icon
                icon="ChevronDown"
                className={cn(
                  'text-cove-dark-grey shrink-0 transition-all duration-500 ease-in-out',

                  expanded && 'text-cove-blue rotate-180'
                )}
              />
            </h5>
          </Button>
          {freeMonths > 1 ? (
            <div className="flex items-center justify-center">
              <Price
                price={parseFloat(`${monitoringBasePrice || 0}`)}
                salePrice={parseFloat(`${monitoringWithDiscountPrice || 0}`)}
                className="text-cove-green mb-0 text-base font-semibold"
              />
              <p className="text-cove-navy-blue ml-cove-5 mb-0 text-right font-semibold">
                {freeMonths} Months Free
              </p>
            </div>
          ) : (
            // Must be a div — Price renders <p>, and <p><p> is invalid HTML that
            // browsers rewrite before hydrate → React #418/#423.
            <div className="text-cove-navy-blue ml-cove-5 mb-0 text-right font-semibold">
              <Price
                price={parseFloat(`${monitoringBasePrice || 0}`)}
                salePrice={parseFloat(`${monitoringWithDiscountPrice || 0}`)}
                className="text-cove-green m-0 text-base font-semibold"
              />
            </div>
          )}
        </div>
        <SmoothMount
          show={expanded}
          initialAnimation={false}
          className="pt-cove-5 flex flex-col"
        >
          <span className="text-cove-navy-blue m-0 text-[12px] font-medium leading-[18px]">
            Auto-pay{' '}
            <span className="text-cove-blue">
              ${totals.monitoringTotalWithTax}
            </span>{' '}
            on the {getNumberWithOrdinal(cart?.getBillingDay?.() ?? 1)} of each
            month
            {freeMonths > 1 && cart?.getBillingDate?.() ? (
              <span
                className="text-cove-navy-blue m-0 text-xs font-medium"
                suppressHydrationWarning
              >
                , starting on{' '}
                {getFullDate(
                  add(cart.getBillingDate(), { months: -1 }),
                  freeMonths
                )}
                .
              </span>
            ) : (
              '.'
            )}
          </span>
          {isTaxCalculated ? (
            <span className="text-cove-navy-blue m-0 text-[12px] font-medium">
              Including tax of:{' '}
              <span className="text-cove-blue">
                ${roundTo(totals?.monitoringTotalTax || 0)}
              </span>
            </span>
          ) : (
            <i className="text-cove-navy-blue m-0 text-[12px]">
              Applicable tax calculated at next step
            </i>
          )}
        </SmoothMount>
      </div>
    </div>
  );
};

const EquipmentBreakdown = ({
  productPackage,
  useOrder = false
}: {
  productPackage?: { name: string; products: [] } | undefined;
  useOrder?: boolean;
}) => {
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const products = useProducts();
  const packageItems = productPackage
    ? productPackage.products.map((product: { sku: string }) => product.sku)
    : [];

  return (
    <>
      {productPackage &&
        `The ${
          productPackage.name.charAt(0).toUpperCase() +
          productPackage.name.slice(1)
        }`}
      <div className="xxs:grid-cols-2 xs:grid-cols-3 grid grid-cols-1">
        {!isUndefined(cart?.cartItemIds) &&
          cart?.cartItemIds?.map((sku) => {
            const product = products[sku];
            const cartProduct = cart?.cartProducts?.[sku];
            if (
              product?.productType === 'equipment' &&
              packageItems.includes(product.sku)
            ) {
              return (
                <p
                  key={product.sku}
                  className="text-cove-navy-blue m-0 text-xs"
                >
                  {cartProduct?.qty} {product.name}
                </p>
              );
            }
          })}
        {cart?.cartItemIds
          ?.map((sku) => products[sku])
          ?.sort((a, b) => a.position - b.position)
          ?.map((product) => {
            const cartProduct = cart?.cartProducts?.[product.sku];
            if (
              product?.productType === 'equipment' &&
              !packageItems.includes(product.sku) &&
              cartProduct &&
              cartProduct?.qty > 0
            ) {
              return (
                <p
                  className="text-cove-navy-blue m-0 text-xs leading-4"
                  key={product.sku}
                >
                  {cartProduct?.qty}{' '}
                  {PRODUCT_DISPLAY_NAMES[
                    product.sku as keyof typeof PRODUCT_DISPLAY_NAMES
                  ] || product.name}
                  {cartProduct?.qty > 1 && product.name.slice(-1) !== 's'
                    ? 's'
                    : ''}
                </p>
              );
            }
          })}
      </div>
    </>
  );
};

const autoAppliedDiscountIdTitleMap = {
  '141785457': 'First month free',
  '141785456': 'First month free',
  '960179': 'Sale'
};

export const PromoCodeBreakdown = ({
  autoAppliedDiscountTitle,
  useOrder = false
}: {
  autoAppliedDiscountTitle?: string;
  useOrder?: boolean;
}) => {
  const [canRemovePromo, setCanRemovePromo] = useState(true);
  const [showPromoList, setShowPromoList] = useState(false);
  const [promoBusy, setPromoBusy] = useState<string | null>(null);
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const [promoCodesApplied, setPromoCodesApplied] = useState(
    cart.discountBreakdown || []
  );

  const hasPromoCode = promoCodesApplied.length > 0;

  useEffect(() => {
    setPromoCodesApplied(cart?.calculateCouponBreakdown?.() || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.coupons?.length, cart?.orderTotals?.appliedDiscountMap]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (hasPromoCode) {
      timeout = setTimeout(() => {
        setShowPromoList(true);
      }, 200);
    } else {
      if (timeout) {
        clearTimeout(timeout);
      }
      setShowPromoList(false);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasPromoCode]);

  const getAverageDiscountPriority = useCallback(
    (discounts: (typeof promoCodesApplied)[number]['discounts']) => {
      return (
        Object.keys(discounts)
          .map(
            (discountId) =>
              currentCart.discounts.find((it) => it.id === discountId)
                ?.priority ?? 0
          )
          .reduce((a, b) => a + b, 0) / Object.keys(discounts).length
      );
    },
    [currentCart]
  );

  const sortPromoCodes = useCallback(
    (
      a: (typeof promoCodesApplied)[number],
      b: (typeof promoCodesApplied)[number]
    ) => {
      const discountA = getAverageDiscountPriority(a.discounts);
      const discountB = getAverageDiscountPriority(b.discounts);

      return discountA - discountB;
    },
    [getAverageDiscountPriority]
  );

  return (
    <MotionConfig>
      <AnimatePresence>
        {hasPromoCode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="xs:mt-cove-15 mt-0 overflow-hidden"
          >
            <div className="p-cove-15 xs:p-cove-25 pt-cove-10 xs:pt-cove-10">
              <h5 className="text-h5">Promo code</h5>
              <div
                className={`mt-cove-10 px-cove-10 overflow-hidden border ${showPromoList ? 'border-cove-dove' : 'border-transparent'} transition-all`}
              >
                <AnimatePresence>
                  {showPromoList &&
                    promoCodesApplied
                      .sort(sortPromoCodes)
                      .map(
                        (
                          {
                            couponCodeId,
                            couponCode,
                            couponCodeDisplayText,
                            totalSavings,
                            description
                          },
                          index
                        ) => {
                          const isAutoAppliedEquipment =
                            couponCodeId === 'auto-applied-960179';
                          const isAutoApplied =
                            couponCodeId?.includes('auto-applied');
                          const strippedPromoCodeId = couponCodeId?.replace(
                            'auto-applied-',
                            ''
                          );
                          return (
                            <motion.div
                              key={couponCode}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div
                                className={cn(
                                  'py-cove-10 bg-white',
                                  index > 0 && 'border-t-cove-dove border-t'
                                )}
                              >
                                <div className="gap-cove-15 md:gap-cove-50 mb-cove-5 flex items-start justify-between text-sm">
                                  <p className="m-0 shrink truncate font-bold">
                                    {isAutoAppliedEquipment &&
                                    autoAppliedDiscountTitle
                                      ? autoAppliedDiscountTitle
                                      : isAutoApplied
                                        ? autoAppliedDiscountIdTitleMap[
                                            strippedPromoCodeId as keyof typeof autoAppliedDiscountIdTitleMap
                                          ] || 'Discount'
                                        : (couponCodeDisplayText || '')
                                              ?.length > 0
                                          ? couponCodeDisplayText
                                          : couponCode}
                                  </p>
                                  {totalSavings > 0 ? (
                                    <p className="text-cove-red m-0 shrink-0 font-normal">
                                      -<PrettyPrice price={totalSavings} />
                                    </p>
                                  ) : null}
                                </div>
                                <div className="gap-cove-50 flex justify-between text-sm">
                                  <p className="m-0">{description}</p>

                                  {!isAutoApplied && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setCanRemovePromo(false);
                                        setPromoBusy(couponCode);
                                        await cart
                                          .removePromo(couponCode)
                                          .then(() => {
                                            setPromoBusy(null);
                                            setCanRemovePromo(true);
                                          });
                                      }}
                                      disabled={!canRemovePromo}
                                      className="m-0 flex w-max min-w-[55px] flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-inherit underline"
                                    >
                                      {promoBusy === couponCode ? (
                                        <Dots
                                          className="bg-cove-blue h-2 w-2"
                                          gap={3}
                                        />
                                      ) : (
                                        'Remove'
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        }
                      )}
                </AnimatePresence>
              </div>
            </div>
            <span className="bg-cove-dove block h-[1px] w-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};

const PromoCodeInput = ({ variant }: { variant: 'base' | 'wide-button' }) => {
  const [error, setError] = useState<string | null>(null);
  const cart = useCart();
  const form = useForm({
    defaultValues: {
      promo: ''
    }
  });

  const { reset, register, setValue, handleSubmit } = form;

  const onSubmit = async (data: { promo: string }) => {
    await cart
      .applyPromo(data.promo)
      .then((res) => {
        setError(null);
        const defaultHandler = (message: string) => setError(message);

        handleApplyCouponResponse(
          res,
          data.promo.toUpperCase(),
          {
            handler: defaultHandler,
            minimumTotal: (message: string) =>
              toast(message, { duration: 8000 }),
            removedCoupons: (message: string) =>
              toast(message, { duration: 8000 })
          },
          cart
        );

        reset();

        return;
      })
      .catch((err) => console.log({ err }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="gap-cove-15 mt-cove-15 sm:mt-cove-25 flex items-start [&_div]:mb-0 [&_div]:min-w-[unset]">
        <Input
          placeholder={
            variant === 'wide-button' ? 'Enter Promo Code' : 'Promo Code'
          }
          {...register('promo', {
            onChange: async (e) => {
              const { value, name } = e.target;
              const normalizedValue = value
                .trim()
                .replace(/[^A-Za-z0-9-]/g, '')
                .toUpperCase();
              setValue(name, normalizedValue);
            }
          })}
          className="rounded-none"
          invalid={!!error}
        />
        <Button
          type="submit"
          variant="outlined"
          disabled={form.formState.isSubmitting}
          loading={form.formState.isSubmitting}
          color={variant === 'wide-button' ? 'navy-blue' : 'blue'}
          className={cn(
            'h-9',
            variant === 'wide-button'
              ? 'w-[160px] border-[1px] font-bold'
              : 'w-[110px] font-medium'
          )}
        >
          Apply
        </Button>
      </div>
      <SmoothMount show={!!error} fade>
        <p className="text-cove-red mb-0 text-sm">{error}</p>
      </SmoothMount>
    </form>
  );
};

type ApplyCouponResponseActions = {
  handler: (message: string) => void;
  exclusive?: (message: string) => void;
  invalid?: (message: string) => void;
  minimumTotal?: (message: string) => void;
  missingProducts?: (message: string) => void;
  noEffect?: (message: string) => void;
  discountsAlreadyApplied?: (message: string) => void;
  alreadyApplied?: (message: string) => void;
  removedCoupons?: (message: string) => void;
  orderTotalTooLow?: (message: string) => void;
  notAvailableInMarket?: (message: string) => void;
};

export const handleApplyCouponResponse = (
  response: Awaited<ReturnType<Cart['applyPromo']>>,
  couponCode: string,
  actions: ApplyCouponResponseActions,
  cart: Cart
) => {
  const {
    isValid,
    isActive,
    allCouponDiscountsAlreadyApplied,
    couponAlreadyApplied,
    missingProducts,
    couponExclusive,
    minimumTotal,
    minimumTotalCoupon,
    hasNoEffect,
    removedCoupons,
    orderTotalTooLow,
    appliesTo,
    notAvailableInMarket
  } = response;

  const getHandler = (handlerName: keyof ApplyCouponResponseActions) =>
    actions[handlerName] ?? actions.handler;

  if (notAvailableInMarket) {
    const region = cart.country === 'CA' ? 'Canada' : 'the United States';
    getHandler('notAvailableInMarket')(
      `This promo code is not available in ${region}.`
    );
    return;
  }

  if (couponExclusive) {
    getHandler('exclusive')('This coupon cannot be combined with other offers');
    return;
  }

  if (!isActive || !isValid) {
    getHandler('invalid')(`Please enter a valid promo code`);
    return;
  }

  if (missingProducts.length > 0) {
    const productNames = missingProducts.map((product) => product.name);

    getHandler('missingProducts')(
      `Your cart is not yet eligible for this coupon. Add a ${createCommaList(productNames)} before applying this coupon.`
    );
    return;
  }

  if (orderTotalTooLow && minimumTotal > 0) {
    getHandler('orderTotalTooLow')(
      `This coupon cannot be applied as it requires a minimum order total of $${roundTo(minimumTotal)}.`
    );
    return;
  }

  if (hasNoEffect && !!minimumTotalCoupon?.minimumTotal) {
    getHandler('noEffect')(
      `"${couponCode.toUpperCase()}" could not be applied because coupon "${minimumTotalCoupon.couponCode}" requires your order total to remain at $${roundTo(minimumTotalCoupon.minimumTotal)} or above. To use "${couponCode.toUpperCase()}", please remove "${minimumTotalCoupon.couponCode}" or add more items to your cart.`
    );
    return;
  }

  if (hasNoEffect) {
    getHandler('handler')(`Please enter a valid promo code`);
    return;
  }

  if (allCouponDiscountsAlreadyApplied) {
    getHandler('discountsAlreadyApplied')(
      'All discounts related to this promo code have already been applied'
    );
    return;
  }

  if (couponAlreadyApplied) {
    getHandler('alreadyApplied')('Promo code already applied');
    return;
  }

  if (
    minimumTotal > 0 &&
    !!minimumTotalCoupon?.minimumTotal &&
    minimumTotalCoupon.minimumTotal >= (cart.totals?.totals?.orderTotal || 0) &&
    !appliesTo?.includes('subscription')
  ) {
    getHandler('minimumTotal')(
      `The coupon has been applied, but ${minimumTotalCoupon.couponCode !== couponCode ? `coupon "${minimumTotalCoupon.couponCode}"` : 'it'} requires a minimum order total of $${minimumTotalCoupon.minimumTotal}. Your order total has been updated.${removedCoupons.length > 0 ? ` ${createCommaList(removedCoupons)} ${removedCoupons.length === 1 ? 'has' : 'have'} been removed because ${removedCoupons.length === 1 ? 'it' : 'they'} had a lower priority or ${removedCoupons.length === 1 ? 'was' : 'were'} not removing anything from the total price.` : ''}`
    );
  }

  cart.calculateCouponBreakdown();

  return;
};

const MAX_GIVEAWAY_TICKETS = 7500;

const GiveawayEntries = () => {
  const { giveawayMultiplier = 1, totals } = useOrderOrCart();
  const tickets = Math.min(
    MAX_GIVEAWAY_TICKETS,
    Math.floor((totals?.totals?.equipmentTotal ?? 0) * giveawayMultiplier)
  );
  // Commenting out until we do another giveaway
  return null;

  return (
    <div className="mb-cove-15 py-cove-5 relative flex items-center justify-between">
      <div className="-inset-x-cove-25 bg-cove-baby-dove absolute inset-y-0"></div>
      <h5 className="text-h5 relative z-10 flex">
        <span>Giveaway entry tickets</span>
        <Modal>
          <Modal.Trigger>
            <Button
              variant="unstyled"
              className="mb-cove-2 ml-cove-10 flex items-center justify-center"
            >
              <Icon icon="Info" width={12} height={12} />
              <p className="sr-only">Giveaway entry tickets disclaimer</p>
            </Button>
          </Modal.Trigger>
          <Modal.Content className="w-auto max-w-[273px] rounded">
            <div className="px-cove-25 py-cove-10 flex items-center justify-center rounded">
              <p className="text-xs leading-tight">
                Every dollar you spend gets you {giveawayMultiplier}{' '}
                {giveawayMultiplier === 1 ? 'entry' : 'entries'} into the
                Protect & Drive giveaway (excluding tax, max of{' '}
                {MAX_GIVEAWAY_TICKETS} entries)
              </p>
            </div>
          </Modal.Content>
        </Modal>
      </h5>
      <div className="gap-cove-10 relative z-10 flex items-center">
        <Icon
          icon="Ticket"
          width={34}
          height={18}
          className="text-cove-dark-grey"
        />{' '}
        <span className="text-cove-blue font-medium">{tickets}</span>
      </div>
    </div>
  );
};

const AvailablePromotions = () => {
  const [expanded, setExpanded] = useState(false);
  const cart = useCart();

  const { isCustomerInfoPage, isPaymentPage } = useCartTotalsPageFlags();

  const availableCoupons = cart.availableCoupons?.filter((coupon) => {
    if (isCustomerInfoPage) return coupon?.showOnCustomerInfoPage;
    if (isPaymentPage) return coupon?.showOnPaymentPage;
    return [];
  });

  if (!availableCoupons?.length) return null;

  return (
    <div className="mt-cove-10 sm:mt-cove-15 flex w-full flex-col">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-cove-blue flex items-center bg-transparent text-sm font-medium"
      >
        See Available Promotions
        <Icon
          className={cn(
            'transition-all duration-500 ease-in-out',
            expanded && 'rotate-180'
          )}
          icon="Caret"
        />
      </button>
      <SmoothMount
        transition={{
          type: 'spring',
          bounce: 0,
          duration: Math.max(0.3, availableCoupons?.length * 0.15)
        }}
        show={expanded}
      >
        {availableCoupons?.map((coupon) => {
          return (
            <div
              key={coupon.couponCode}
              className="gap-cove-15 pt-cove-5 flex flex-col"
            >
              <Promotion coupon={coupon} />
            </div>
          );
        })}
      </SmoothMount>
    </div>
  );
};

const Promotion = ({
  coupon
}: {
  coupon: AvailableCouponsBreakdown[number];
}) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [loading, setLoading] = useState(false);

  const cart = useCart();
  const couponApplied = cart.coupons.some(
    (cpn) => cpn.couponCode === coupon.couponCode.toUpperCase()
  );

  const handleTogglePromo = () => {
    setLoading(true);
    if (couponApplied) {
      cart.removePromo(coupon.couponCode).then(() => setLoading(false));
    } else {
      cart
        .applyPromo(coupon.couponCode)
        .then((res) => {
          const handler = (message: string) => toast(message);

          handleApplyCouponResponse(
            res,
            coupon.couponCode.toUpperCase(),
            {
              handler
            },
            cart
          );
          return;
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className="p-cove-15 border-cove-navy-blue/25 gap-cove-5 flex w-full flex-col border">
      <div className="flex w-full items-center justify-between">
        <div className="gap-cove-5 flex items-center justify-start">
          <p className="text-cove-navy-blue text-sm font-semibold">
            {coupon.couponCode}
          </p>
          {coupon.disclaimer && (
            <Modal open={showDisclaimer} onOpenChange={setShowDisclaimer}>
              <Modal.Trigger>
                <Button
                  variant="unstyled"
                  className="mb-cove-2"
                  onClick={() => {
                    setShowDisclaimer(true);
                  }}
                >
                  <Icon icon="Info" width={12} height={12} />
                  <p className="sr-only">{coupon.couponCode} disclaimer</p>
                </Button>
              </Modal.Trigger>
              <Modal.Content className="w-[95%] rounded sm:w-auto">
                <div className="p-cove-25 flex items-center justify-center rounded">
                  {coupon.disclaimer}
                </div>
              </Modal.Content>
            </Modal>
          )}
        </div>
        <p
          className={cn(
            'text-sm font-semibold',
            coupon.totalSavings > 0 ? 'text-cove-red' : 'text-cove-navy-blue'
          )}
        >
          {coupon.totalSavings > 0 ? <>-${coupon.totalSavings}</> : 'FREE'}
        </p>
      </div>
      <div className="gap-cove-10 flex w-full items-center justify-between">
        <p className="text-cove-navy-blue text-[12px] font-normal">
          {coupon.description}
        </p>
        <Button
          variant="outlined"
          className={cn(
            'text-cove-navy-blue border-cove-navy-blue py-cove-2 gap-cove-5 max-w-cove-100 flex h-6 items-center justify-center rounded-full border bg-[#F9FDFD] px-2 text-[12px] font-semibold',
            couponApplied && 'bg-cove-navy-blue text-white'
          )}
          onClick={handleTogglePromo}
          disabled={loading}
        >
          {loading ? (
            <Dots
              className={cn('w-cove-5 h-cove-5', couponApplied && 'bg-white')}
            />
          ) : (
            <>
              <Icon
                icon="Check"
                className={cn(
                  'text-cove-navy-blue hidden max-w-3',
                  couponApplied && 'flex'
                )}
              />
              {couponApplied ? 'Redeemed' : 'Redeem'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export {
  EquipmentBreakdown,
  EquipmentDetails,
  GiveawayEntries,
  Monitoring,
  PromoCodeInput,
  AvailablePromotions
};
export default CartTotalsWidget;
